import { ReactElement, useEffect, useState } from "react";
import {
  IDatetimeRange,
  IDictionaryNumericKey,
  IProduction, IShiftHistory,
  IStrandProduction
} from "../../../types";
import AxisHorizontal from "../../../components/charts/AxisHorizontal";
import { getTicks, useChartDimensions, useUniqueId } from "../../../components/charts/chartUtils";
import {
  formatDateTimeWithSeconds,
  formatTimeWithSeconds,
  getCurrentDatetimeInSeconds
} from "../../../system/utils";
import Chart from "../../../components/charts/chart";
import * as d3 from "d3";
import { IMillProductDetail } from "../types";
import ProductionChartCoil from "./productionChartCoil";
import ProductionChartMachine from "./productionChartMachine";
import ProductionChartMachineBorder from "./productionChartMachineBorder";
import DelayLayerGantt from "../../interruptions/compoenents/delayLayerGantt";
import { getPalette } from "../../../configuration/configuration";

interface IProps {
  productionData: IProduction[];
  productDetail: IMillProductDetail[];
  shiftHistory: IShiftHistory[];
}


const ProductionChart = ({ productionData, productDetail, shiftHistory }: IProps): ReactElement => {
  const [detail, setDetail] = useState<IDictionaryNumericKey<IMillProductDetail[]> | null>(null);
  const [ref, dimensions] = useChartDimensions<HTMLDivElement>({
    height: 120,
    marginBottom: 30,
    marginLeft: 110,
    marginRight: 9,
    marginTop: 10
  });

  useEffect(() => {
    if (!productDetail) {
      setDetail(null);

      return;
    }

    const dict: IDictionaryNumericKey<IMillProductDetail[]> = {};

    productDetail.forEach(item => {
      if (!dict[item.productionMachineId]) {
        dict[item.productionMachineId] = [ item ];
      } else {
        dict[item.productionMachineId].push(item);
      }
    });

    setDetail(dict);
  }, [productDetail]);

  let maximumEndDelay = 0;
  productionData[productionData.length - 1].productionMachines.forEach(machine => {
    const endTimeList = machine.delays.map(d => d.endDatetime ?? getCurrentDatetimeInSeconds());
    maximumEndDelay = Math.max(maximumEndDelay, ...endTimeList);
  });

  const begin = Math.min(...shiftHistory.map(x => x.beginDatetime));
  const end = Math.max(...shiftHistory.map(x => x.endDatetime));

  const xScale = d3.scaleLinear()
    .domain([begin, end])
    .range([0, dimensions.boundedWidth]);

  const xAccessorScaled = (d: IDatetimeRange) => xScale(d.beginDatetime);
  const widthAccessorScaled = (d: IDatetimeRange) => Math.max(0, xScale(d.endDatetime ?? getCurrentDatetimeInSeconds()) - xScale(d.beginDatetime));
  const chartID = useUniqueId("management-chart");
  const h4 = dimensions.boundedHeight / 4.0;

  const now = getCurrentDatetimeInSeconds();
  const xCursor = xScale(now);

  return (
    <div className="flex-grow-0" style={{ height: 160, width: "100%" }}>
      <div ref={ref} style={{ height: 140, width: "100%" }}>
        <Chart dimensions={dimensions}>

          <AxisHorizontal
            chartID={chartID}
            dimensions={dimensions}
            scale={xScale}
            label={"Datetime"}
            ticks={getTicks(xScale.domain(), 5)}
            formatTick={(value, index) => index === 0 || index === 4 ? formatDateTimeWithSeconds(value as number) : formatTimeWithSeconds(value as number)}/>
          <g>
            {productionData.map((production, index) => {
              const prodKey = `${chartID}-prod-${index}`;

              return (
                <g key={prodKey}>
                  {production.productionMachines.map((machine, mindex) => {
                    const machineKey = `${prodKey}-${machine.machine.name}-${mindex}`;

                    const endTimeList = machine.delays.map(d => d.endDatetime ?? getCurrentDatetimeInSeconds());
                    const machineEnd = Math.max(...endTimeList, productionData[productionData.length - 1].endDatetime ?? getCurrentDatetimeInSeconds());

                    const x = xAccessorScaled(machine as IStrandProduction);
                    const w = Math.max(xScale(machineEnd) - xScale(machine.beginDatetime) - 1, 0);
                    const y = machine.machine.code.toLocaleLowerCase() === "strand1" ? h4: h4 * 2;
                    const h = h4 - 1;
                    const itemDetail = detail ? detail[machine.productionMachineID] : undefined;

                    let totalDelays = 0;
                    machine.delays.filter(xyz => !xyz.isDownDay).forEach(delay => {
                      totalDelays += delay.duration ?? 0;
                    });

                    const barInMill = ((machine.endDatetime ?? getCurrentDatetimeInSeconds()) - machine.beginDatetime - totalDelays) / 60;

                    return(
                      <g key={machineKey}>
                        <ProductionChartMachine key={machineKey} productionItem={machine as IStrandProduction} x={x} y={y} w={w} h={h} barInMill={barInMill} />

                        <DelayLayerGantt delays={machine.delays} height={h} y={y} xAccessorScaled={xAccessorScaled}
                          widthAccessorScaled={widthAccessorScaled} selected={null} chartKey={chartID} />

                        <ProductionChartMachineBorder productionItem={machine as IStrandProduction} x={x} y={y} w={w} h={h}  />

                        {itemDetail && <ProductionChartCoil machine={machine as IStrandProduction} machineKey={machineKey} h4={h4} xScale={xScale} itemDetail={itemDetail}/>}

                        <rect
                          style={{ fill: getPalette().statusColor.gray.background }}
                          x={xCursor}
                          y={y}
                          width={Math.max(0, dimensions.boundedWidth - xCursor)}
                          height={h} />
                      </g>
                    );
                  })
                  }
                </g>
              );
            })
            }
          </g>

          <text x={-100} y={h4 + 12} className="Axis__tick">Strand 1</text>
          <text x={-100} y={h4 * 2 + 12} className="Axis__tick">Strand 2</text>

          <g clipPath="url(#cut-off)">
            {xCursor >= 0 && xCursor <= dimensions.boundedWidth &&
            <>
              <line
                x1={xCursor}
                x2={xCursor}
                y1={0}
                y2={dimensions.boundedHeight}
                strokeWidth={2}
                stroke="#333"
                strokeDasharray="4"
              />

              <text
                className="Axis__tick"
                x={xCursor + 2}
                y={10}
                fontSize=".8em"
                textAnchor={"start"}>
                Now
              </text>

            </>
            }
          </g>
        </Chart>
      </div>
    </div>
  );
};

export default ProductionChart;
